"use client";

import { useEffect, useRef, useState } from "react";
import Image from "next/image";
import { motion, AnimatePresence } from "motion/react";

const ROTATION_MS = 4500;
const TRANSITION_S = 0.9;

export interface CarouselImage {
  src: string;
  alt: string;
}

interface WindowItem {
  id: number;
  image: CarouselImage;
  variant: "center" | "side";
}

// Center-weighted, auto-advancing showcase for Devina's real photos -- deliberately no arrows/
// dots/swipe handling (see page.tsx's About()): this is a decorative rotation next to her bio,
// not a browsable gallery someone needs to control. Every image is a real photo she shared (see
// public/images/), never a stock placeholder.
//
// Each visible slide is a persistent element tracked by a stable id, not by screen position --
// when the set advances, the photo sliding into the center slot grows into it and the old
// center photo shrinks as it moves to a side slot (Motion's `layout` prop FLIP-animates the
// width/position change caused by a slide's variant switching, rather than swapping the whole
// row as one rigid block). Only the two far ends actually mount/unmount each tick -- their
// slide-and-fade enter/exit is handled by AnimatePresence -- which is what makes the middle
// transition read as one continuous grow/shrink instead of a hard cut. MotionConfig's
// `reducedMotion="user"` (set once, globally, in layout.tsx) automatically drops this motion
// for anyone with the OS "reduce motion" preference on.
let uid = 0;

function windowFor(images: CarouselImage[], centerIndex: number): WindowItem[] {
  const at = (offset: number) => images[(centerIndex + offset + images.length) % images.length];
  return [
    { id: uid++, image: at(-1), variant: "side" },
    { id: uid++, image: at(0), variant: "center" },
    { id: uid++, image: at(1), variant: "side" },
  ];
}

export default function PhotoCarousel({
  images,
  compact = false,
}: {
  images: CarouselImage[];
  // Smaller center/side cards at `lg` for a full-width usage (InstagramSection) where the
  // default size (tuned for sitting in a half-width column next to Devina's bio in About())
  // would make this, not the surrounding text, the tallest thing on a one-viewport-tall desktop
  // screen -- see page.tsx's InstagramSection. `lg`-scoped, not unconditional: below that the
  // one-viewport goal doesn't apply, and the default size reads better on a real phone width.
  compact?: boolean;
}) {
  const cursor = useRef(0);
  const [items, setItems] = useState<WindowItem[]>(() => (images.length ? windowFor(images, 0) : []));

  useEffect(() => {
    cursor.current = 0;
    setItems(images.length ? windowFor(images, 0) : []);
  }, [images]);

  useEffect(() => {
    if (images.length <= 1) return;
    const id = setInterval(() => {
      cursor.current = (cursor.current + 1) % images.length;
      const newNext = images[(cursor.current + 1) % images.length];
      setItems((prev) => [
        { ...prev[1], variant: "side" },
        { ...prev[2], variant: "center" },
        { id: uid++, image: newNext, variant: "side" },
      ]);
    }, ROTATION_MS);
    return () => clearInterval(id);
  }, [images]);

  if (images.length === 0) return null;

  return (
    // overflow-hidden here is load-bearing, not decorative -- each Slide animates in/out on the
    // x axis (+-60px), and without this, a slide mid-transition can stick out past the edge of
    // the viewport on a narrow screen and briefly widen the whole page's scrollable area (the
    // intermittent horizontal-scroll/wobble this fixed).
    <div className="flex items-center justify-center gap-3 overflow-hidden sm:gap-4">
      <AnimatePresence initial={false}>
        {items.map((item) => (
          <Slide key={item.id} image={item.image} variant={item.variant} compact={compact} />
        ))}
      </AnimatePresence>
    </div>
  );
}

function Slide({
  image,
  variant,
  compact,
}: {
  image: CarouselImage;
  variant: "center" | "side";
  compact: boolean;
}) {
  const isCenter = variant === "center";
  return (
    <motion.div
      layout
      initial={{ opacity: 0, x: 60 }}
      animate={{ opacity: isCenter ? 1 : 0.55, x: 0 }}
      exit={{ opacity: 0, x: -60 }}
      transition={{ duration: TRANSITION_S, ease: "easeInOut" }}
      className={
        isCenter
          ? `relative aspect-[4/5] w-[42%] ${compact ? "lg:w-[30%]" : ""} shrink-0 overflow-hidden rounded-2xl shadow-xl`
          : `relative aspect-[4/5] w-[24%] ${compact ? "lg:w-[17%]" : ""} shrink-0 overflow-hidden rounded-xl`
      }
    >
      <motion.div
        className="absolute inset-0"
        initial={{ scale: 1 }}
        animate={{ scale: isCenter ? 1.12 : 1.06 }}
        transition={{ duration: ROTATION_MS / 1000 + TRANSITION_S, ease: "easeInOut" }}
      >
        {/* Below the fold on every page this renders on -- lazy by default (no `priority`)
            is the correct performance call here, not an oversight. */}
        <Image
          src={image.src}
          alt={image.alt}
          fill
          sizes={isCenter ? "(max-width: 640px) 45vw, 340px" : "(max-width: 640px) 24vw, 190px"}
          className="object-cover"
        />
      </motion.div>
    </motion.div>
  );
}
